import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useNotification } from "@/contexts/NotificationContext";
import LoadingOverlay from "@/components/overlays/LoadingOverlay";
import { api, del, errorMessage, post, put } from "@/lib/api";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import { History } from "lucide-react";
import "./RoleDefaults.css";

type RoleFlagKey =
  | "superAdmin"
  | "admin"
  | "teacher"
  | "student"
  | "curriculumDeveloper"
  | "counselor"
  | "parent";

type RecipeKey = RoleFlagKey | "globalAdmin";

type RoleFlags = Record<RoleFlagKey, boolean>;

type InstructorKitFlags = {
  proposeActivities: boolean;
  editActivities: boolean;
  gradeActivities: boolean;
  overrideActivities: boolean;
};

type SupervisorKitFlags = {
  viewActivities: boolean;
  gradeActivities: boolean;
  overrideActivities: boolean;
  studentProgressChat: boolean;
  staffProgressChat: boolean;
};

type MessageKinds = { office: boolean };

type LinkTypeFlags = {
  guardians: boolean;
  counselors: boolean;
  siblings: boolean;
};

type RelationshipTypeFlags = {
  guardian: boolean;
  sibling: boolean;
  teacher_of_record: boolean;
  counselor: boolean;
  supervisor: boolean;
};

type AttendanceActionFlags = {
  insert: boolean;
  update: boolean;
};

type EventActionFlags = {
  insert: boolean;
  edit: boolean;
  remove: boolean;
};

type SourceKitFlags = {
  document: boolean;
  image: boolean;
  audio: boolean;
  video: boolean;
  url: boolean;
  replaceFile: boolean;
  editAnalysis: boolean;
  max12mb: boolean;
  max80mb: boolean;
  max200mb: boolean;
};

interface RolePermissions {
  pages: string[];
  capabilities: string[];
  addUserRoles: RoleFlags;
  viewUserRoles: RoleFlags;
  inboxRoles: RoleFlags;
  instructorKit: InstructorKitFlags;
  supervisorKit: SupervisorKitFlags;
  messageKinds: MessageKinds;
  linkTypes: LinkTypeFlags;
  relationshipTypes: RelationshipTypeFlags;
  sourceKit: SourceKitFlags;
  eventActions: EventActionFlags;
  attendanceActions: AttendanceActionFlags;
}

interface CatalogPage {
  id: string;
  label: string;
  description: string;
  hideFromNav?: boolean;
}

interface CatalogCapability {
  id: string;
  label: string;
  description: string;
  group: string;
  groupLabel: string;
  groupHint?: string;
  addRoles?: boolean;
  viewRoles?: boolean;
  inboxRoles?: boolean;
  instructorKit?: boolean;
  supervisorKit?: boolean;
  messageKinds?: boolean;
  linkTypes?: boolean;
  relationshipTypes?: boolean;
  sourceKit?: boolean;
  eventActions?: boolean;
  attendanceActions?: boolean;
  kitOptions?: { id: string; slug: string; label: string; disabled?: boolean }[];
}

interface RoleFlagOption {
  id: RoleFlagKey;
  label: string;
}

interface HistoryChange {
  op: "added" | "removed";
  kind: string;
  id: string;
}

interface HistoryEvent {
  at: string | null;
  action: string;
  byUisUserId: string | null;
  byUisUgpId: string | null;
  changes: HistoryChange[];
}

interface RoleRow {
  roleKey: RecipeKey;
  label: string;
  identity?: RoleFlagKey;
  identityCap?: string | null;
  identityHint?: string;
  note?: string;
  source: "template" | "saved";
  matchesTemplate?: boolean;
  permissions: RolePermissions;
  createdByUisUserId: string | null;
  createdByUisUgpId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  updateHistory: HistoryEvent[];
}

interface RoleDefaultsPayload {
  catalog: {
    pages: CatalogPage[];
    capabilities: CatalogCapability[];
    roleFlags: RoleFlagOption[];
  };
  roles: RoleRow[];
}

interface UisCompareTeam {
  granteeType: string;
  granteeId: string;
  label: string;
  identityHint?: string;
  inferred: RecipeKey | null;
  live: string[];
  missing: string[];
  extra: string[];
}

interface UisCompareRecipe {
  roleKey: RecipeKey;
  label: string;
  expected: string[];
  teamCount: number;
  matchedCount?: number;
  mismatchedCount?: number;
  teams: UisCompareTeam[];
}

interface UisComparePayload {
  note: string;
  recipes: UisCompareRecipe[];
  teams?: UisCompareTeam[];
  unclassified: UisCompareTeam[];
}

const PAGE_ID = "role-defaults";

function emptyRoleFlags(): RoleFlags {
  return {
    superAdmin: false,
    admin: false,
    teacher: false,
    student: false,
    curriculumDeveloper: false,
    counselor: false,
    parent: false,
  };
}

function emptyInstructorKit(on = false): InstructorKitFlags {
  return {
    proposeActivities: on,
    editActivities: on,
    gradeActivities: on,
    overrideActivities: on,
  };
}

function emptySupervisorKit(on = false): SupervisorKitFlags {
  return {
    viewActivities: on,
    gradeActivities: on,
    overrideActivities: on,
    studentProgressChat: on,
    staffProgressChat: on,
  };
}

function emptyEventActions(on = false): EventActionFlags {
  return { insert: on, edit: on, remove: on };
}

function emptyAttendanceActions(on = false): AttendanceActionFlags {
  return { insert: on, update: on };
}

function emptyLinkTypes(on?: Partial<LinkTypeFlags>): LinkTypeFlags {
  return {
    guardians: on?.guardians === true,
    counselors: on?.counselors === true,
    siblings: on?.siblings === true,
  };
}

function emptyRelationshipTypes(on?: Partial<RelationshipTypeFlags>): RelationshipTypeFlags {
  return {
    guardian: on?.guardian === true,
    sibling: on?.sibling === true,
    teacher_of_record: on?.teacher_of_record === true,
    counselor: on?.counselor === true,
    supervisor: on?.supervisor === true,
  };
}

function relationshipTypesUnset(flags: RelationshipTypeFlags | undefined): boolean {
  return !flags?.guardian && !flags?.sibling && !flags?.teacher_of_record && !flags?.counselor && !flags?.supervisor;
}

function emptySourceKit(on = false): SourceKitFlags {
  return {
    document: on,
    image: on,
    audio: on,
    video: on,
    url: on,
    replaceFile: on,
    editAnalysis: on,
    max12mb: false,
    max80mb: false,
    max200mb: false,
  };
}

function sourceKitUnset(flags: SourceKitFlags | undefined): boolean {
  if (!flags) return true;
  return !(
    flags.document ||
    flags.image ||
    flags.audio ||
    flags.video ||
    flags.url ||
    flags.replaceFile ||
    flags.editAnalysis
  );
}

function applyNestedDefaults(next: RolePermissions, cap: CatalogCapability | undefined) {
  if (!cap) return;
  if (cap.messageKinds && !next.messageKinds?.office) next.messageKinds = { office: true };
  if (
    cap.linkTypes &&
    !next.linkTypes?.guardians &&
    !next.linkTypes?.counselors &&
    !next.linkTypes?.siblings
  ) {
    next.linkTypes = emptyLinkTypes({ guardians: true, counselors: true });
  }
  if (cap.relationshipTypes && relationshipTypesUnset(next.relationshipTypes)) {
    next.relationshipTypes = emptyRelationshipTypes({ guardian: true, counselor: true });
  }
  if (cap.sourceKit && sourceKitUnset(next.sourceKit)) next.sourceKit = emptySourceKit(true);
  if (cap.eventActions && kitAllOff(next.eventActions)) next.eventActions = emptyEventActions(true);
  if (cap.attendanceActions && kitAllOff(next.attendanceActions)) {
    next.attendanceActions = emptyAttendanceActions(true);
  }
}

function messageKindHint(id: string): string {
  if (id === "messages.SendMessages") {
    return "Office is required. They may start an office request and view the threads they started. This does not open the office inbox. Support is not stored. This Office box is shared by Receive, Send, and Reply on this role.";
  }
  if (id === "messages.ReplyMessages") {
    return "Office is required. Checked kinds are the inboxes they answer as staff. A parent continuing their own note uses Message Students Office. Support is not stored. This Office box is shared by Receive, Send, and Reply on this role.";
  }
  return "Office is required. They can read the office inbox. This does not start a thread and does not reply. Support is not stored. This Office box is shared by Receive, Send, and Reply on this role.";
}

function kitAllOff(flags: Record<string, boolean> | undefined): boolean {
  return !flags || Object.values(flags).every((on) => !on);
}

function clonePermissions(value: RolePermissions): RolePermissions {
  return {
    pages: [...value.pages],
    capabilities: [...value.capabilities],
    addUserRoles: { ...value.addUserRoles },
    viewUserRoles: { ...value.viewUserRoles },
    inboxRoles: { ...emptyRoleFlags(), ...value.inboxRoles },
    instructorKit: { ...emptyInstructorKit(), ...value.instructorKit },
    supervisorKit: { ...emptySupervisorKit(), ...value.supervisorKit },
    messageKinds: { office: value.messageKinds?.office === true },
    linkTypes: { ...emptyLinkTypes(), ...value.linkTypes },
    relationshipTypes: { ...emptyRelationshipTypes(), ...value.relationshipTypes },
    sourceKit: { ...emptySourceKit(), ...value.sourceKit },
    eventActions: { ...emptyEventActions(), ...value.eventActions },
    attendanceActions: { ...emptyAttendanceActions(), ...value.attendanceActions },
  };
}

function fingerprint(value: RolePermissions): string {
  return JSON.stringify({
    pages: [...value.pages].sort(),
    capabilities: [...value.capabilities].sort(),
    addUserRoles: value.addUserRoles,
    viewUserRoles: value.viewUserRoles,
    inboxRoles: value.inboxRoles,
    instructorKit: value.instructorKit,
    supervisorKit: value.supervisorKit,
    messageKinds: value.messageKinds,
    linkTypes: value.linkTypes,
    relationshipTypes: value.relationshipTypes,
    sourceKit: value.sourceKit,
    eventActions: value.eventActions,
    attendanceActions: value.attendanceActions,
  });
}

function toggleId(list: string[], id: string, on: boolean): string[] {
  const next = new Set(list);
  if (on) next.add(id);
  else next.delete(id);
  return [...next];
}

function kindLabel(kind: string): string {
  if (kind === "page") return "page";
  if (kind === "capability") return "capability";
  if (kind === "addUserRole") return "add-role";
  if (kind === "viewUserRole") return "view-role";
  if (kind === "inboxRole") return "inbox role";
  if (kind === "instructorKit") return "enrolled kit";
  if (kind === "supervisorKit") return "without-enrollment kit";
  if (kind === "messageKind") return "message kind";
  if (kind === "linkType") return "link type";
  if (kind === "sourceKit") return "source kit";
  if (kind === "eventActions") return "event action";
  if (kind === "attendanceActions") return "attendance action";
  return kind || "permission";
}

function formatWhen(at: string | null): string {
  if (!at) return "";
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return at;
  return date.toLocaleString();
}

function textMatches(needle: string, ...parts: Array<string | undefined>) {
  if (!needle) return false;
  return parts.some((part) => (part || "").toLowerCase().includes(needle));
}

function PermissionRow({
  id,
  label,
  description,
  checked,
  onToggle,
  expand,
  flash,
  children,
}: {
  id: string;
  label: string;
  description?: string;
  checked: boolean;
  onToggle: (on: boolean) => void;
  expand?: boolean;
  flash?: boolean;
  children?: ReactNode;
}) {
  return (
    <li data-permission-id={id}>
      <div className={`al-role-defaults-row${flash ? " al-role-defaults-row--flash" : ""}`}>
        <label className="al-role-defaults-check">
          <input
            type="checkbox"
            checked={checked}
            onChange={(event) => onToggle(event.target.checked)}
          />
          <span className="al-role-defaults-check-label">{label}</span>
        </label>
        <details className="al-role-defaults-more" key={expand ? "open" : "shut"} open={expand || undefined}>
          <summary>Details</summary>
          <span className="al-role-defaults-check-id">{id}</span>
          {description ? <span className="al-role-defaults-check-help">{description}</span> : null}
        </details>
      </div>
      {children}
    </li>
  );
}

function teamKey(team: UisCompareTeam): string {
  return `${team.granteeType}:${team.granteeId}`;
}

function pairDiff(expected: string[], live: string[]) {
  const have = new Set(live);
  const want = new Set(expected);
  return {
    missing: expected.filter((id) => !have.has(id)),
    extra: live.filter((id) => !want.has(id)),
  };
}

type DiffKind = "missing" | "extra";

function diffRowKey(kind: DiffKind, id: string) {
  return `${kind}:${id}`;
}

function DiffList({
  missing,
  extra,
  busy,
  onApply,
}: {
  missing: string[];
  extra: string[];
  busy?: boolean;
  onApply: (next: { missing: string[]; extra: string[] }) => void;
}) {
  const rows = useMemo(
    () => [
      ...missing.map((id) => ({ kind: "missing" as const, id, key: diffRowKey("missing", id) })),
      ...extra.map((id) => ({ kind: "extra" as const, id, key: diffRowKey("extra", id) })),
    ],
    [missing, extra],
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const dragRef = useRef<{
    pointerId: number;
    anchor: string;
    additive: boolean;
    origin: Set<string>;
    moved: boolean;
  } | null>(null);

  useEffect(() => {
    const live = new Set(rows.map((row) => row.key));
    setSelected((current) => {
      const next = [...current].filter((key) => live.has(key));
      if (next.length === current.size && next.every((key) => current.has(key))) return current;
      return new Set(next);
    });
  }, [rows]);

  if (!rows.length) {
    return <p className="al-role-defaults-diff-match">Match</p>;
  }

  const rangeKeys = (from: string, to: string) => {
    const start = rows.findIndex((row) => row.key === from);
    const end = rows.findIndex((row) => row.key === to);
    if (start < 0) return [to];
    if (end < 0) return [from];
    const lo = Math.min(start, end);
    const hi = Math.max(start, end);
    return rows.slice(lo, hi + 1).map((row) => row.key);
  };

  const keyAtPoint = (x: number, y: number) => {
    const node = document.elementFromPoint(x, y)?.closest("[data-diff-key]");
    return node instanceof HTMLElement ? node.dataset.diffKey || "" : "";
  };

  const paintTo = (key: string) => {
    const drag = dragRef.current;
    if (!drag || !key) return;
    const painted = rangeKeys(drag.anchor, key);
    if (drag.additive) {
      setSelected(new Set([...drag.origin, ...painted]));
      return;
    }
    setSelected(new Set(painted));
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>, key: string) => {
    if (event.button !== 0) return;
    const additive = event.metaKey || event.ctrlKey;
    const shift = event.shiftKey;
    if (shift) {
      const anchor = dragRef.current?.anchor || [...selected][0] || key;
      dragRef.current = {
        pointerId: event.pointerId,
        anchor,
        additive,
        origin: new Set(selected),
        moved: false,
      };
      paintTo(key);
    } else if (additive) {
      setSelected((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      dragRef.current = {
        pointerId: event.pointerId,
        anchor: key,
        additive: true,
        origin: selected.has(key)
          ? new Set([...selected].filter((item) => item !== key))
          : new Set([...selected, key]),
        moved: false,
      };
    } else {
      if (!selected.has(key)) setSelected(new Set([key]));
      dragRef.current = {
        pointerId: event.pointerId,
        anchor: key,
        additive: false,
        origin: new Set(selected.has(key) ? selected : [key]),
        moved: false,
      };
    }
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const key = keyAtPoint(event.clientX, event.clientY);
    if (!key || key === drag.anchor) return;
    drag.moved = true;
    paintTo(key);
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = { ...drag, pointerId: -1 };
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* already released */
    }
  };

  const applyKeys = (keys: Set<string>) => {
    const want = keys.size ? keys : new Set<string>();
    onApply({
      missing: rows.filter((row) => row.kind === "missing" && want.has(row.key)).map((row) => row.id),
      extra: rows.filter((row) => row.kind === "extra" && want.has(row.key)).map((row) => row.id),
    });
  };

  return (
    <>
      {selected.size > 1 ? (
        <p className="al-role-defaults-diff-stat">{selected.size} selected</p>
      ) : null}
      <ul
        className="al-role-defaults-diff"
        aria-label="UIS diff"
      >
        {rows.map((row) => {
          const isSelected = selected.has(row.key);
          return (
            <li
              key={row.key}
              data-diff-key={row.key}
              className={row.kind === "missing" ? "al-role-defaults-diff--del" : "al-role-defaults-diff--add"}
            >
              <button
                type="button"
                className={`al-role-defaults-diff-hit${isSelected ? " is-selected" : ""}`}
                disabled={busy}
                title={
                  row.kind === "missing"
                    ? "Drag to select. Double-click to grant on the UIS group."
                    : "Drag to select. Double-click to check on this Learn kit."
                }
                onPointerDown={(event) => onPointerDown(event, row.key)}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onDoubleClick={() => {
                  const keys = selected.has(row.key) && selected.size ? selected : new Set([row.key]);
                  applyKeys(keys);
                }}
              >
                <span className="al-role-defaults-diff-mark" aria-hidden>
                  {row.kind === "missing" ? "−" : "+"}
                </span>
                <code>{row.id}</code>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

const COMPARE_POS_KEY = "al-role-defaults-compare-pos";

function readComparePos(): { x: number; y: number } {
  try {
    const raw = sessionStorage.getItem(COMPARE_POS_KEY);
    if (!raw) return { x: 0, y: 0 };
    const parsed = JSON.parse(raw) as { x?: number; y?: number };
    return {
      x: Number.isFinite(parsed.x) ? Number(parsed.x) : 0,
      y: Number.isFinite(parsed.y) ? Number(parsed.y) : 0,
    };
  } catch {
    return { x: 0, y: 0 };
  }
}

export default function RoleDefaults() {
  const { pageAccess, loading: authLoading } = useAuth();
  const { toast, notice } = useNotification();
  const canOpen = Boolean(pageAccess?.[PAGE_ID]);

  const [payload, setPayload] = useState<RoleDefaultsPayload | null>(null);
  const [drafts, setDrafts] = useState<Record<string, RolePermissions>>({});
  const [activeKey, setActiveKey] = useState<RecipeKey>("admin");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [compare, setCompare] = useState<UisComparePayload | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareTried, setCompareTried] = useState(false);
  const [selectedTeamKey, setSelectedTeamKey] = useState("");
  const [grantingId, setGrantingId] = useState("");
  const [flashId, setFlashId] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [comparePos, setComparePos] = useState(readComparePos);
  const compareRef = useRef<HTMLElement | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const envelope = await api<{ success: boolean; data: RoleDefaultsPayload }>("/api/role-defaults");
      const data = envelope.data;
      setPayload(data);
      const nextDrafts: Record<string, RolePermissions> = {};
      for (const row of data.roles) {
        nextDrafts[row.roleKey] = clonePermissions(row.permissions);
      }
      setDrafts(nextDrafts);
      setActiveKey((current) =>
        data.roles.some((row) => row.roleKey === current) ? current : data.roles[0].roleKey,
      );
    } catch (err) {
      notice({
        type: "error",
        title: "Could not load role defaults",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setLoading(false);
    }
  }, [notice]);

  const applyComparePayload = useCallback(
    (data: UisComparePayload, roleKey: RecipeKey) => {
      const teams = data.teams?.length
        ? data.teams
        : [...data.recipes.flatMap((row) => row.teams), ...data.unclassified];
      setCompare({ ...data, teams });
      setSelectedTeamKey((current) => {
        if (current && teams.some((team) => teamKey(team) === current)) return current;
        const inferred = teams.find((team) => team.inferred === roleKey);
        return inferred ? teamKey(inferred) : teams[0] ? teamKey(teams[0]) : "";
      });
    },
    [],
  );

  const loadCompare = useCallback(
    async (open = true) => {
      setCompareTried(true);
      setCompareLoading(true);
      try {
        const envelope = await api<{ success: boolean; data: UisComparePayload }>(
          "/api/role-defaults/uis-compare",
        );
        applyComparePayload(envelope.data, activeKey);
        if (open) setCompareOpen(true);
      } catch (err) {
        notice({
          type: "error",
          title: "Could not compare with UIS",
          subtext: errorMessage(err),
          duration: 6000,
        });
      } finally {
        setCompareLoading(false);
      }
    },
    [activeKey, applyComparePayload, notice],
  );

  useEffect(() => {
    if (authLoading || !canOpen) return;
    void load();
  }, [authLoading, canOpen, load]);

  useEffect(() => {
    if (authLoading || !canOpen || !payload || compare || compareLoading || compareTried) return;
    void loadCompare(false);
  }, [authLoading, canOpen, payload, compare, compareLoading, compareTried, loadCompare]);

  const active = payload?.roles.find((row) => row.roleKey === activeKey) || null;
  const draft = active ? drafts[active.roleKey] : null;
  const dirty = Boolean(active && draft && fingerprint(draft) !== fingerprint(active.permissions));
  const needle = query.trim().toLowerCase();

  const compareTeams = compare?.teams || [];
  const selectedTeam = compareTeams.find((team) => teamKey(team) === selectedTeamKey) || null;
  const expectedKeys = draft
    ? [...draft.pages, ...draft.capabilities].sort((a, b) => a.localeCompare(b))
    : [];
  const pair = selectedTeam ? pairDiff(expectedKeys, selectedTeam.live) : null;

  const visiblePages = useMemo(() => {
    const pages = payload?.catalog.pages || [];
    if (!needle) return pages;
    return pages.filter(
      (page) =>
        page.label.toLowerCase().includes(needle) ||
        page.id.toLowerCase().includes(needle) ||
        page.description.toLowerCase().includes(needle),
    );
  }, [payload, needle]);

  const capabilityGroups = useMemo(() => {
    const caps = payload?.catalog.capabilities || [];
    const filtered = needle
      ? caps.filter(
          (cap) =>
            cap.label.toLowerCase().includes(needle) ||
            cap.id.toLowerCase().includes(needle) ||
            cap.description.toLowerCase().includes(needle) ||
            (cap.kitOptions || []).some(
              (opt) =>
                opt.label.toLowerCase().includes(needle) ||
                opt.id.toLowerCase().includes(needle) ||
                opt.slug.toLowerCase().includes(needle),
            ),
        )
      : caps;
    const groups: { key: string; label: string; hint: string; items: CatalogCapability[] }[] = [];
    const index = new Map<string, number>();
    for (const cap of filtered) {
      let at = index.get(cap.group);
      if (at == null) {
        at = groups.length;
        index.set(cap.group, at);
        groups.push({ key: cap.group, label: cap.groupLabel, hint: cap.groupHint || "", items: [] });
      }
      groups[at].items.push(cap);
    }
    return groups;
  }, [payload, needle]);

  useEffect(() => {
    if (!flashId) return;
    const node = document.querySelector(`[data-permission-id="${CSS.escape(flashId)}"]`);
    node?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    const timer = window.setTimeout(() => setFlashId(""), 1600);
    return () => window.clearTimeout(timer);
  }, [flashId]);

  const clampComparePos = useCallback((next: { x: number; y: number }) => {
    const panel = compareRef.current;
    const width = panel?.offsetWidth || 320;
    const height = panel?.offsetHeight || 200;
    const left = window.innerWidth - 16 - width + next.x;
    const top = 16 + next.y;
    const clampedLeft = Math.min(window.innerWidth - width - 8, Math.max(8, left));
    const clampedTop = Math.min(window.innerHeight - height - 8, Math.max(8, top));
    return {
      x: clampedLeft - (window.innerWidth - 16 - width),
      y: clampedTop - 16,
    };
  }, []);

  const onCompareDragStart = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    if ((event.target as Element | null)?.closest("button")) return;
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: comparePos.x,
      originY: comparePos.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onCompareDragMove = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const next = clampComparePos({
      x: drag.originX + (event.clientX - drag.startX),
      y: drag.originY + (event.clientY - drag.startY),
    });
    setComparePos(next);
  };

  const onCompareDragEnd = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* already released */
    }
    setComparePos((current) => {
      const next = clampComparePos(current);
      try {
        sessionStorage.setItem(COMPARE_POS_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / private mode */
      }
      return next;
    });
  };

  const patchDraft = (roleKey: string, updater: (current: RolePermissions) => RolePermissions) => {
    setDrafts((current) => {
      const prior = current[roleKey];
      if (!prior) return current;
      return { ...current, [roleKey]: updater(prior) };
    });
  };

  const kitSlot = (id: string): "pages" | "capabilities" | null => {
    if (payload?.catalog.pages.some((page) => page.id === id)) return "pages";
    if (payload?.catalog.capabilities.some((cap) => cap.id === id)) return "capabilities";
    return null;
  };

  const checkKitKeys = (ids: string[]) => {
    if (!active) return;
    const unique = [...new Set(ids.filter(Boolean))];
    if (!unique.length) return;
    const unknown = unique.filter((id) => !kitSlot(id));
    const known = unique.filter((id) => kitSlot(id));
    if (!known.length) {
      notice({
        type: "warning",
        title: "Not on the Learn kit catalog",
        subtext: unknown[0] || "",
        duration: 5000,
      });
      return;
    }
    patchDraft(active.roleKey, (current) => {
      const next: RolePermissions = {
        ...current,
        pages: [...current.pages],
        capabilities: [...current.capabilities],
        inboxRoles: { ...emptyRoleFlags(), ...current.inboxRoles },
        instructorKit: { ...current.instructorKit },
        supervisorKit: { ...current.supervisorKit },
        messageKinds: { ...current.messageKinds },
        linkTypes: { ...current.linkTypes },
        relationshipTypes: { ...emptyRelationshipTypes(), ...current.relationshipTypes },
        sourceKit: { ...current.sourceKit },
        eventActions: { ...current.eventActions },
        attendanceActions: { ...current.attendanceActions },
      };
      for (const id of known) {
        const slot = kitSlot(id);
        if (!slot) continue;
        next[slot] = toggleId(next[slot], id, true);
        if (slot === "capabilities") {
          const cap = payload?.catalog.capabilities.find((item) => item.id === id);
          if (cap?.instructorKit && kitAllOff(next.instructorKit)) {
            next.instructorKit = emptyInstructorKit(true);
          }
          if (cap?.supervisorKit && kitAllOff(next.supervisorKit)) {
            next.supervisorKit = emptySupervisorKit(true);
          }
          if (cap?.eventActions && kitAllOff(next.eventActions)) {
            next.eventActions = emptyEventActions(true);
          }
          if (cap?.attendanceActions && kitAllOff(next.attendanceActions)) {
            next.attendanceActions = emptyAttendanceActions(true);
          }
          applyNestedDefaults(next, cap);
        }
      }
      return next;
    });
    setFlashId(known[known.length - 1]);
    toast.success(
      known.length === 1 ? `Checked ${known[0]} on ${active.label}` : `Checked ${known.length} keys on ${active.label}`,
    );
    if (unknown.length) {
      notice({
        type: "warning",
        title: "Skipped keys not on the Learn kit catalog",
        subtext: unknown.join(", "),
        duration: 5000,
      });
    }
  };

  const grantUisKeys = async (ids: string[]) => {
    if (!selectedTeam) return;
    const unique = [...new Set(ids.filter(Boolean))];
    if (!unique.length) return;
    const granted: string[] = [];
    const failed: { id: string; message: string }[] = [];
    for (const id of unique) {
      setGrantingId(id);
      try {
        const envelope = await post<{ success: boolean; data: { resourceId: string } }>(
          "/api/role-defaults/uis-grant",
          {
            granteeType: selectedTeam.granteeType,
            granteeId: selectedTeam.granteeId,
            resourceId: id,
            addUserRoles: draft?.addUserRoles,
            viewUserRoles: draft?.viewUserRoles,
            inboxRoles: draft?.inboxRoles,
            instructorKit: draft?.instructorKit,
            supervisorKit: draft?.supervisorKit,
            messageKinds: draft?.messageKinds,
            linkTypes: draft?.linkTypes,
            relationshipTypes: draft?.relationshipTypes,
            sourceKit: draft?.sourceKit,
            eventActions: draft?.eventActions,
            attendanceActions: draft?.attendanceActions,
          },
        );
        granted.push(envelope.data?.resourceId || id);
      } catch (err) {
        failed.push({ id, message: errorMessage(err) });
      }
    }
    if (granted.length) {
      const added = new Set(granted);
      setCompare((current) => {
        if (!current?.teams) return current;
        return {
          ...current,
          teams: current.teams.map((team) =>
            teamKey(team) === selectedTeamKey
              ? { ...team, live: [...new Set([...team.live, ...added])].sort() }
              : team,
          ),
        };
      });
      toast.success(
        granted.length === 1
          ? `Granted ${granted[0]} on ${selectedTeam.label}`
          : `Granted ${granted.length} keys on ${selectedTeam.label}`,
      );
    }
    if (failed.length) {
      notice({
        type: "error",
        title:
          failed.length === 1
            ? `Could not grant ${failed[0].id}`
            : `${failed.length} keys did not grant`,
        subtext: failed.map((row) => `${row.id}: ${row.message}`).join(" · "),
        duration: 8000,
      });
    }
    setGrantingId("");
  };

  const applyDiffSelection = (next: { missing: string[]; extra: string[] }) => {
    if (next.extra.length) checkKitKeys(next.extra);
    if (next.missing.length) void grantUisKeys(next.missing);
  };

  const save = async () => {
    if (!active || !draft) return;
    if (
      draft.capabilities.includes("dm.SendStudentRelationship") &&
      relationshipTypesUnset(draft.relationshipTypes)
    ) {
      notice({
        type: "error",
        title: "Send Student Relationship requires a person-link type",
        subtext: "Check guardian, sibling, teacher of record, counselor, or supervisor. None checked means this role cannot message those people.",
        duration: 6000,
      });
      return;
    }
    if (
      draft.capabilities.includes("dm.ViewOthersInboxes") &&
      !Object.values(draft.inboxRoles || {}).some(Boolean)
    ) {
      notice({
        type: "error",
        title: "View Others Inboxes requires at least one role",
        subtext: "Check whose inboxes this role may read. That list is other people’s messages, not this role’s own Messages.",
        duration: 6000,
      });
      return;
    }
    setSaving(true);
    try {
      const envelope = await put<{ success: boolean; data: RoleRow }>(
        `/api/role-defaults/${encodeURIComponent(active.roleKey)}`,
        { permissions: draft },
      );
      const saved = envelope.data;
      setPayload((current) =>
        current
          ? {
              ...current,
              roles: current.roles.map((row) => (row.roleKey === saved.roleKey ? saved : row)),
            }
          : current,
      );
      setDrafts((current) => ({
        ...current,
        [saved.roleKey]: clonePermissions(saved.permissions),
      }));
      toast.success(`Saved ${saved.label} defaults`);
    } catch (err) {
      notice({
        type: "error",
        title: "Could not save",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const envelope = await del<{ success: boolean; data: RoleRow }>(
        `/api/role-defaults/${encodeURIComponent(active.roleKey)}`,
      );
      const restored = envelope.data;
      setPayload((current) =>
        current
          ? {
              ...current,
              roles: current.roles.map((row) => (row.roleKey === restored.roleKey ? restored : row)),
            }
          : current,
      );
      setDrafts((current) => ({
        ...current,
        [restored.roleKey]: clonePermissions(restored.permissions),
      }));
      toast.success(`Reset ${restored.label} to the built-in template`);
    } catch (err) {
      notice({
        type: "error",
        title: "Could not reset",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || (canOpen && loading && !payload)) {
    return <LoadingOverlay isVisible message="Loading role defaults…" />;
  }

  if (!canOpen) {
    return <Navigate to="/" replace state={accessDeniedNavigateState(PAGE_ID)} />;
  }

  const roleFlags = payload?.catalog.roleFlags || [];

  return (
    <div className="al-role-defaults">
      <LoadingOverlay
        isVisible={saving || compareLoading || Boolean(grantingId)}
        message={compareLoading ? "Comparing with UIS…" : grantingId ? "Granting on UIS…" : "Saving…"}
      />
      <header className="al-role-defaults-header">
        <div>
          <h1>Role defaults</h1>
          <p className="al-role-defaults-sub">
            Compare one UIS group to one Learn kit. School Admin and System Admin share{" "}
            <code>role.IsAdmin</code>; System Admin also has <code>scope.Global</code>.
          </p>
        </div>
      </header>

      <div className="al-role-defaults-pair">
        <label className="al-role-defaults-badge-field">
          <span>UIS group</span>
          <select
            value={selectedTeamKey}
            disabled={compareLoading || !compareTeams.length}
            onChange={(event) => setSelectedTeamKey(event.target.value)}
          >
            {!compareTeams.length ? (
              <option value="">{compareLoading ? "Loading groups…" : "No identity groups yet"}</option>
            ) : null}
            {compareTeams.map((team) => (
              <option key={teamKey(team)} value={teamKey(team)}>
                {team.identityHint ? `${team.identityHint} — ${team.label}` : team.label}
              </option>
            ))}
          </select>
        </label>
        <label className="al-role-defaults-badge-field">
          <span>Learn kit</span>
          <select
            value={activeKey}
            onChange={(event) => {
              setActiveKey(event.target.value as RecipeKey);
              setHistoryOpen(false);
            }}
          >
            {(payload?.roles || []).map((row) => (
              <option key={row.roleKey} value={row.roleKey}>
                {row.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="al-role-defaults-btn"
          disabled={compareLoading}
          onClick={() => void loadCompare(true)}
        >
          {compareLoading ? "Comparing…" : "Compare"}
        </button>
      </div>

      {active && draft ? (
        <>
          <div className="al-role-defaults-meta">
            {active.identityHint || active.identityCap ? (
              <span className="al-role-defaults-pill">{active.identityHint || active.identityCap}</span>
            ) : null}
            <span className={active.source === "saved" ? "al-role-defaults-pill" : "al-role-defaults-pill al-role-defaults-pill--muted"}>
              {active.source === "saved" ? "Saved in Learn" : "Built-in template (not saved yet)"}
            </span>
            {dirty ? <span className="al-role-defaults-pill al-role-defaults-pill--warn">Unsaved</span> : null}
            {active.source === "saved" ? (
              <button
                type="button"
                className={`al-role-defaults-icon-btn${historyOpen ? " is-on" : ""}`}
                aria-label={historyOpen ? "Hide history" : "Show history"}
                aria-expanded={historyOpen}
                title="History"
                onClick={() => setHistoryOpen((open) => !open)}
              >
                <History size={14} strokeWidth={2} aria-hidden="true" />
              </button>
            ) : null}
            <label className="al-role-defaults-filter">
              <span className="al-role-defaults-sr">Filter permissions</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Filter pages and capabilities"
              />
            </label>
            <div className="al-role-defaults-actions">
              <button
                type="button"
                className="al-role-defaults-btn al-role-defaults-btn--ghost"
                disabled={saving || active.source === "template" || (Boolean(active.matchesTemplate) && !dirty)}
                onClick={() => void reset()}
              >
                Reset
              </button>
              <button
                type="button"
                className="al-role-defaults-btn"
                disabled={saving || (active.source !== "template" && !dirty)}
                onClick={() => void save()}
              >
                Save
              </button>
            </div>
          </div>

          {active.note ? <p className="al-role-defaults-note">{active.note}</p> : null}

          {compareOpen ? (
            <section
              ref={compareRef}
              className="al-role-defaults-compare al-role-defaults-compare--float"
              aria-label="UIS compare"
              style={{ transform: `translate(${comparePos.x}px, ${comparePos.y}px)` }}
            >
              <div
                className="al-role-defaults-compare-head"
                onPointerDown={onCompareDragStart}
                onPointerMove={onCompareDragMove}
                onPointerUp={onCompareDragEnd}
                onPointerCancel={onCompareDragEnd}
              >
                <h2>
                  {selectedTeam ? selectedTeam.label : "UIS group"}
                  {" ↔ "}
                  {active.label}
                </h2>
                <button
                  type="button"
                  className="al-role-defaults-compare-close"
                  aria-label="Close compare"
                  onClick={() => setCompareOpen(false)}
                >
                  Close
                </button>
              </div>
              <p className="al-role-defaults-diff-legend" aria-label="Diff legend">
                <span className="al-role-defaults-diff--del">
                  <span className="al-role-defaults-diff-mark" aria-hidden>
                    −
                  </span>
                  not on UIS
                </span>
                <span className="al-role-defaults-diff--add">
                  <span className="al-role-defaults-diff-mark" aria-hidden>
                    +
                  </span>
                  on UIS, not in this Learn kit
                </span>
              </p>
              <p className="al-role-defaults-compare-hint">
                Drag to select several. Double-click − to grant those keys on the UIS group.
                Double-click + to check them on this Learn kit, then Save.
              </p>
              {!selectedTeam || !pair ? (
                <p className="al-role-defaults-empty">
                  Pick a UIS group with a role identity (for example role.IsAdmin on Admins —
                  Permanent), then Compare.
                </p>
              ) : (
                <>
                  {selectedTeam.identityHint ? (
                    <p className="al-role-defaults-note">
                      UIS identity {selectedTeam.identityHint}
                      {selectedTeam.inferred && selectedTeam.inferred !== active.roleKey
                        ? ` · that group looks like ${
                            payload?.roles.find((row) => row.roleKey === selectedTeam.inferred)?.label ||
                            selectedTeam.inferred
                          }, you are comparing it to ${active.label}`
                        : ""}
                    </p>
                  ) : null}
                  <div className="al-role-defaults-diff-stat">
                    {pair.missing.length ? (
                      <span className="al-role-defaults-diff--del">−{pair.missing.length}</span>
                    ) : null}
                    {pair.extra.length ? (
                      <span className="al-role-defaults-diff--add">+{pair.extra.length}</span>
                    ) : null}
                    {!pair.missing.length && !pair.extra.length ? "match" : null}
                  </div>
                  <DiffList
                    key={selectedTeamKey}
                    missing={pair.missing}
                    extra={pair.extra}
                    busy={Boolean(grantingId)}
                    onApply={applyDiffSelection}
                  />
                </>
              )}
              <div className="al-role-defaults-compare-foot">
                <button
                  type="button"
                  className="al-role-defaults-btn al-role-defaults-btn--ghost"
                  disabled={saving || active.source === "template" || (Boolean(active.matchesTemplate) && !dirty)}
                  onClick={() => void reset()}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="al-role-defaults-btn"
                  disabled={saving || (active.source !== "template" && !dirty)}
                  onClick={() => void save()}
                >
                  Save
                </button>
              </div>
            </section>
          ) : null}

          {active.source === "saved" && historyOpen ? (
            <ol className="al-role-defaults-history" aria-label="Update history">
              <li>
                Created
                {active.createdAt ? ` · ${formatWhen(active.createdAt)}` : ""}
                {active.createdByUisUserId ? ` · ${active.createdByUisUserId}` : ""}
              </li>
              {[...active.updateHistory].reverse().map((event, index) => (
                <li key={`${event.at || "event"}:${index}`}>
                  <span className="al-role-defaults-history-when">
                    {event.action === "reset" ? "Reset to template" : "Updated"}
                    {event.at ? ` · ${formatWhen(event.at)}` : ""}
                    {event.byUisUserId ? ` · ${event.byUisUserId}` : ""}
                  </span>
                  <ul>
                    {event.changes.map((change) => (
                      <li key={`${change.op}:${change.kind}:${change.id}`}>
                        {change.op === "removed" ? "Removed" : "Added"} {kindLabel(change.kind)}{" "}
                        <code>{change.id}</code>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          ) : null}

          <div className="al-role-defaults-grid">
            <section className="al-role-defaults-panel">
              <h2>Pages</h2>
              <ul className="al-role-defaults-list">
                {visiblePages.map((page) => (
                  <PermissionRow
                    key={page.id}
                    id={page.id}
                    label={page.label}
                    description={page.description}
                    checked={draft.pages.includes(page.id)}
                    flash={flashId === page.id}
                    expand={textMatches(needle, page.id, page.label, page.description) || flashId === page.id}
                    onToggle={(on) =>
                      patchDraft(active.roleKey, (current) => ({
                        ...current,
                        pages: toggleId(current.pages, page.id, on),
                      }))
                    }
                  />
                ))}
              </ul>
            </section>

            <section className="al-role-defaults-panel">
              <h2>Capabilities</h2>
              {capabilityGroups.map((group) => {
                const onCount = group.items.filter((cap) => draft.capabilities.includes(cap.id)).length;
                return (
                  <details
                    key={group.key}
                    className="al-role-defaults-group"
                    open={needle || group.items.some((cap) => cap.id === flashId) ? true : undefined}
                  >
                    <summary>
                      <span className="al-role-defaults-group-heading">
                        <span className="al-role-defaults-group-name">{group.label}</span>
                        {group.hint ? <span className="al-role-defaults-group-hint">{group.hint}</span> : null}
                      </span>
                      <span className="al-role-defaults-group-count">
                        {onCount}/{group.items.length}
                      </span>
                    </summary>
                    <ul className="al-role-defaults-list">
                      {group.items.map((cap) => {
                        const checked = draft.capabilities.includes(cap.id);
                        const kitField = cap.instructorKit
                          ? "instructorKit"
                          : cap.supervisorKit
                            ? "supervisorKit"
                            : cap.eventActions
                              ? "eventActions"
                              : cap.attendanceActions
                                ? "attendanceActions"
                                : null;
                        return (
                          <PermissionRow
                            key={cap.id}
                            id={cap.id}
                            label={cap.label}
                            description={cap.description}
                            checked={checked}
                            flash={flashId === cap.id}
                            expand={
                              textMatches(
                                needle,
                                cap.id,
                                cap.label,
                                cap.description,
                                ...(cap.kitOptions || []).flatMap((opt) => [opt.label, opt.id, opt.slug]),
                              ) || flashId === cap.id
                            }
                            onToggle={(on) =>
                              patchDraft(active.roleKey, (current) => {
                                const next: RolePermissions = {
                                  ...current,
                                  capabilities: toggleId(current.capabilities, cap.id, on),
                                };
                                if (on && cap.instructorKit && kitAllOff(current.instructorKit)) {
                                  next.instructorKit = emptyInstructorKit(true);
                                }
                                  if (on && cap.supervisorKit && kitAllOff(current.supervisorKit)) {
                                    next.supervisorKit = emptySupervisorKit(true);
                                  }
                                  if (on && cap.eventActions && kitAllOff(current.eventActions)) {
                                    next.eventActions = emptyEventActions(true);
                                  }
                                  if (on && cap.attendanceActions && kitAllOff(current.attendanceActions)) {
                                    next.attendanceActions = emptyAttendanceActions(true);
                                  }
                                if (on) applyNestedDefaults(next, cap);
                                return next;
                              })
                            }
                          >
                            {checked && kitField && cap.kitOptions?.length ? (
                              <div className="al-role-defaults-nested">
                                <p>
                                  {cap.eventActions
                                    ? "Create, edit, and delete. Leave all three checked to allow every verb. Uncheck all to allow none."
                                    : cap.attendanceActions
                                      ? "The first mark, and changing a mark. Clearing is a change. Leave both checked to allow both."
                                      : cap.instructorKit
                                        ? "Ceiling for enrolled copies. Narrows UIS Teach Enrolled — the same teacher can still differ per course."
                                        : "Kit for copies they are not enrolled on. Narrows UIS Teach Without Enrollment — not propose or live edit."}
                                </p>
                                <div className="al-role-defaults-flags">
                                  {cap.kitOptions.map((opt) => {
                                    const kit = draft[kitField] as Record<string, boolean>;
                                    const on = kit[opt.id] === true;
                                    return (
                                      <label key={`${cap.id}:${opt.id}`}>
                                        <input
                                          type="checkbox"
                                          checked={on}
                                          onChange={(event) =>
                                            patchDraft(active.roleKey, (current) => ({
                                              ...current,
                                              [kitField]: {
                                                ...current[kitField],
                                                [opt.id]: event.target.checked,
                                              },
                                            }))
                                          }
                                        />
                                        {opt.label}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : checked && (cap.addRoles || cap.viewRoles) ? (
                              <div className="al-role-defaults-nested">
                                <p>
                                  {cap.addRoles
                                    ? "Which identities this role may add"
                                    : "Which identities this role may view"}
                                </p>
                                <div className="al-role-defaults-flags">
                                  {roleFlags
                                    .filter((flag) => !(cap.addRoles && flag.id === "superAdmin"))
                                    .map((flag) => {
                                    const field = cap.addRoles ? "addUserRoles" : "viewUserRoles";
                                    const on = draft[field][flag.id];
                                    return (
                                      <label key={`${cap.id}:${flag.id}`}>
                                        <input
                                          type="checkbox"
                                          checked={on}
                                          onChange={(event) =>
                                            patchDraft(active.roleKey, (current) => ({
                                              ...current,
                                              [field]: {
                                                ...current[field],
                                                [flag.id]: event.target.checked,
                                              },
                                            }))
                                          }
                                        />
                                        {flag.label}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : checked && cap.inboxRoles ? (
                              <div className="al-role-defaults-nested">
                                <p>
                                  Whose inboxes this role may read. At least one role. These are other people’s
                                  direct messages, not this role’s own Messages.
                                </p>
                                <div className="al-role-defaults-flags">
                                  {roleFlags.map((flag) => {
                                    const on = draft.inboxRoles[flag.id] === true;
                                    return (
                                      <label key={`${cap.id}:${flag.id}`}>
                                        <input
                                          type="checkbox"
                                          checked={on}
                                          onChange={(event) =>
                                            patchDraft(active.roleKey, (current) => ({
                                              ...current,
                                              inboxRoles: {
                                                ...emptyRoleFlags(),
                                                ...current.inboxRoles,
                                                [flag.id]: event.target.checked,
                                              },
                                            }))
                                          }
                                        />
                                        {flag.label}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : checked && cap.messageKinds && cap.kitOptions?.length ? (
                              <div className="al-role-defaults-nested">
                                <p>{messageKindHint(cap.id)}</p>
                                <div className="al-role-defaults-flags">
                                  {cap.kitOptions.map((opt) =>
                                    opt.disabled ? (
                                      <label key={`${cap.id}:${opt.id}`} className="is-locked">
                                        <input type="checkbox" checked={false} disabled />
                                        {opt.label}
                                      </label>
                                    ) : (
                                      <label key={`${cap.id}:${opt.id}`}>
                                        <input
                                          type="checkbox"
                                          checked={draft.messageKinds.office === true}
                                          onChange={(event) =>
                                            patchDraft(active.roleKey, (current) => ({
                                              ...current,
                                              messageKinds: { office: event.target.checked },
                                            }))
                                          }
                                        />
                                        {opt.label}
                                      </label>
                                    ),
                                  )}
                                </div>
                              </div>
                            ) : checked && cap.linkTypes && cap.kitOptions?.length ? (
                              <div className="al-role-defaults-nested">
                                <p>
                                  Which relationships this role may link. Parents are guardians, not a separate type.
                                  At least one is required.
                                </p>
                                <div className="al-role-defaults-flags">
                                  {cap.kitOptions.map((opt) => (
                                    <label key={`${cap.id}:${opt.id}`}>
                                      <input
                                        type="checkbox"
                                        checked={draft.linkTypes[opt.id as keyof LinkTypeFlags] === true}
                                        onChange={(event) =>
                                          patchDraft(active.roleKey, (current) => ({
                                            ...current,
                                            linkTypes: {
                                              ...current.linkTypes,
                                              [opt.id]: event.target.checked,
                                            },
                                          }))
                                        }
                                      />
                                      {opt.label}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ) : checked && cap.relationshipTypes && cap.kitOptions?.length ? (
                              <div className="al-role-defaults-nested">
                                <p>
                                  Which person links this role may direct-message. The student is not a type. At least
                                  one is required.
                                </p>
                                <div className="al-role-defaults-flags">
                                  {cap.kitOptions.map((opt) => (
                                    <label key={`${cap.id}:${opt.id}`}>
                                      <input
                                        type="checkbox"
                                        checked={draft.relationshipTypes[opt.id as keyof RelationshipTypeFlags] === true}
                                        onChange={(event) =>
                                          patchDraft(active.roleKey, (current) => ({
                                            ...current,
                                            relationshipTypes: {
                                              ...emptyRelationshipTypes(),
                                              ...current.relationshipTypes,
                                              [opt.id]: event.target.checked,
                                            },
                                          }))
                                        }
                                      />
                                      {opt.label}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ) : checked && cap.sourceKit && cap.kitOptions?.length ? (
                              <div className="al-role-defaults-nested">
                                <p>
                                  Ceiling for Add, Edit, and Propose on this role. Leave the size boxes off to use the
                                  Learn defaults.
                                </p>
                                <div className="al-role-defaults-flags">
                                  {cap.kitOptions.map((opt) => (
                                    <label key={`${cap.id}:${opt.id}`}>
                                      <input
                                        type="checkbox"
                                        checked={draft.sourceKit[opt.id as keyof SourceKitFlags] === true}
                                        onChange={(event) =>
                                          patchDraft(active.roleKey, (current) => ({
                                            ...current,
                                            sourceKit: {
                                              ...current.sourceKit,
                                              [opt.id]: event.target.checked,
                                            },
                                          }))
                                        }
                                      />
                                      {opt.label}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </PermissionRow>
                        );
                      })}
                    </ul>
                  </details>
                );
              })}
            </section>
          </div>
        </>
      ) : (
        <p className="al-role-defaults-empty">No roles loaded.</p>
      )}
    </div>
  );
}
